/* Candidate modal quote. The server alone determines charges and setup reuse. */
(() => {
  'use strict';
  const states = new WeakMap(), files = new WeakMap();
  let nextFile = 0;
  const money = cents => `$${(cents / 100).toFixed(2)}`;
  function signature(product, state) {
    if (state.file && !files.has(state.file)) files.set(state.file, ++nextFile);
    const variant = product.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    return JSON.stringify([variant, state.textOn, state.text, state.color, state.textPlacement, state.logoOn,
      state.method, state.logoPlacement, state.assetId, state.file ? files.get(state.file) : null]);
  }
  function incomplete(state) {
    if (!state.textOn && !state.logoOn) return 'Choose text, a logo, or both to see your quote.';
    if (state.textOn && (!state.text?.trim() || !state.color)) return 'Enter your text and choose a thread color to see your quote.';
    if (state.logoOn && ((!state.file && !state.assetId) || !state.method)) return 'Choose a logo and application method to see your quote.';
    if (state.logoOn && state.file && (!['image/png', 'image/jpeg'].includes(state.file.type) || state.file.size > 10 * 1024 * 1024)) return 'Choose a PNG or JPEG no larger than 10 MB.';
    return '';
  }
  function render({container, productInfo, state, client = window.wcfPersonalizationCheckout}) {
    if (!client?.enabled || !client.fixedSetCount(productInfo)) return false;
    let entry = states.get(container);
    if (!entry) { entry = {generation: 0, timer: null, signature: null, quote: null}; states.set(container, entry); }
    entry.latest = {container, productInfo, state: {...state}, client};
    const message = incomplete(state), key = signature(productInfo, state);
    if (message) {
      clearTimeout(entry.timer); entry.generation++; entry.signature = null; entry.quote = null;
      container.textContent = message;
      return true;
    }
    if (entry.signature === key && (!entry.quote || entry.quote.expiresAt > Date.now())) return true;
    clearTimeout(entry.timer); entry.signature = key; entry.quote = null;
    const generation = ++entry.generation, snapshot = {...state};
    container.textContent = 'Updating quote…';
    entry.timer = setTimeout(async () => {
      try {
        const quote = await client.quoteSelection(productInfo, snapshot, 1);
        if (generation !== entry.generation || !container.isConnected || entry.signature !== signature(productInfo, entry.latest.state)) return;
        entry.quote = quote;
        const ref = quote.preview?.newLineRef, line = quote.lines.find(item => item.lineRef === ref);
        if (!line || !Number.isSafeInteger(quote.preview?.cartTotalCents)) throw new Error('Your quote could not be verified.');
        const fees = component => quote.fees.filter(fee => fee.component === component && (component === 'setup' || fee.properties?._pc_line === ref)).reduce((total, fee) => total + fee.quantity * fee.unitCents, 0);
        const count = client.fixedSetCount(productInfo), setLabel = `${count} ${count === 1 ? 'set' : 'sets'}`;
        const lines = [`Garment (${setLabel}): ${money(line.quantity * line.unitCents)}`];
        if (snapshot.textOn) lines.push(`Text embroidery: ${money(fees('text'))}`);
        if (snapshot.logoOn) lines.push(`Logo: ${money(fees('logo'))}`);
        if (snapshot.method === 'heat' || quote.fees.some(fee => fee.component === 'setup')) lines.push(`${quote.preview.existingCart ? 'Setup for these logos' : 'Heat transfer setup'}: ${money(fees('setup'))}`);
        lines.push(quote.preview.existingCart ? `Cart total with this selection: ${money(quote.preview.cartTotalCents)}` : `Total for ${setLabel}: ${money(quote.preview.cartTotalCents)}`);
        container.replaceChildren(...lines.map((line, index) => {
          const row = container.ownerDocument.createElement('div'); row.textContent = line;
          if (index === lines.length - 1) row.className = 'wcf-preview-total';
          return row;
        }));
        if (client.guestSessionNotice) {
          const notice = container.ownerDocument.createElement('p'); notice.className = 'wcf-preview-note'; notice.textContent = client.guestSessionNotice;
          container.append(notice);
        }
      } catch (error) {
        if (generation !== entry.generation || !container.isConnected || entry.signature !== signature(productInfo, entry.latest.state)) return;
        container.textContent = error.message || 'Your quote could not be updated.';
        const retry = container.ownerDocument.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry quote';
        retry.addEventListener('click', () => { entry.signature = null; render(entry.latest); });
        container.append(retry);
      }
    }, 400);
    return true;
  }
  function dispose(container) {
    const entry = states.get(container);
    if (entry) { clearTimeout(entry.timer); entry.generation++; states.delete(container); }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {render, dispose};
  if (typeof window !== 'undefined') window.wcfPersonalizationQuotePreview = Object.freeze({render, dispose});
})();
